import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useFavorites } from "../context/FavoritesContext";
import { Search, Package, Plus, Minus, ShoppingCart, ImageOff, X, ChevronDown, ChevronUp, Heart, FileDown, Ruler, Bell, StickyNote } from "lucide-react";
import { api, imgUrl, errDetail, unitOf, unitsTotal, UNIT_NAME } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LazyImage } from "../components/LazyImage";

function ProductCard({ p, onZoom, isFav, onToggleFav, alertOn, onToggleAlert, showNote, note, onSaveNote }) {
  const [noteDraft, setNoteDraft] = useState(note || "");
  useEffect(() => { setNoteDraft(note || ""); }, [note]);
  const { items, setQty } = useCart();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const { t } = useLang();
  const inCart = items.find((i) => i.product.id === p.id);
  const [qty, setLocalQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const unit = unitOf(p);

  const add = async () => {
    setBusy(true);
    try {
      await setQty(p.id, (inCart?.qty_boxes || 0) + (Number(qty) || 1));
      toast.success(`${p.reference}: ${qty} ${t("product.boxes")} ${t("toast.added")}`);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="product-card group bg-card border border-border rounded-2xl overflow-hidden flex flex-col hover:shadow-lg transition-shadow" data-testid={`product-card-${p.reference}`}>
      <div className="aspect-square bg-muted overflow-hidden relative">
        {p.image_url ? (
          <LazyImage src={imgUrl(p.image_url)} alt={p.name} onClick={() => onZoom(p)} testid={`product-image-${p.reference}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageOff size={32} /></div>
        )}
        <span className="absolute top-2 left-2 bg-slate-900/85 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md">{p.reference}</span>
        {p.out_of_stock && (
          <span className="absolute bottom-2 left-2 bg-red-600 text-white text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-md shadow" data-testid={`catalog-out-of-stock-${p.reference}`}>{t("product.outOfStock")}</span>
        )}
        <button onClick={() => onToggleFav(p)} data-testid={`fav-toggle-${p.reference}`} title={isFav ? t("product.unfav") : t("product.fav")}
          aria-pressed={isFav}
          className={`absolute top-2 right-2 h-8 w-8 rounded-full flex items-center justify-center shadow transition-transform hover:scale-110 ${isFav ? "bg-rose-500 text-white" : "bg-white/90 text-slate-700 hover:text-rose-500"}`}>
          <Heart size={15} fill={isFav ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="p-3 sm:p-4 flex flex-col justify-between flex-1 gap-2">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-600">{p.category}</span>
          <h3 className="font-semibold text-sm leading-snug line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <div>
              <span className="text-lg font-bold font-heading" data-testid={`product-price-${p.reference}`}>${p.price_dozen.toFixed(2)}</span>
              <span className="text-xs text-muted-foreground"> /{UNIT_NAME[unit] || unit}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Package size={13} /> {p.packing > 0 ? `${p.packing} ${unit}/caja` : t("product.packingNA")}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground min-h-[1rem]" data-testid={`product-size-${p.reference}`}>
            {p.size ? <><Ruler size={13} /> {t("product.size")}: {p.size}</> : null}
          </div>
          {p.out_of_stock && !isStaff && user?.role !== "admin" ? (
            <button onClick={() => onToggleAlert(p)} data-testid={`restock-alert-${p.reference}`} aria-pressed={alertOn}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-semibold border transition-colors ${alertOn ? "bg-emerald-600 text-white border-transparent" : "border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-600/10"}`}>
              <Bell size={13} fill={alertOn ? "currentColor" : "none"} /> {alertOn ? t("fav.notifyOn") : t("fav.notifyMe")}
            </button>
          ) : (
          <div className="flex items-center gap-2 pt-1" style={{ display: isStaff ? "none" : undefined }}>
            <div className="flex flex-col items-center">
              <div className="flex items-center border border-border rounded-full">
                <button onClick={() => setLocalQty(Math.max(1, (Number(qty) || 1) - 1))} className="p-1.5 hover:bg-muted rounded-l-full" data-testid={`qty-minus-${p.reference}`}><Minus size={13} /></button>
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={qty} data-testid={`qty-value-${p.reference}`}
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setLocalQty(v === "" ? "" : Math.max(1, parseInt(v, 10))); }}
                  onBlur={() => { if (!qty) setLocalQty(1); }}
                  className="w-10 px-1 py-1 text-sm font-semibold text-center bg-transparent focus:outline-none focus:ring-1 focus:ring-amber-600 rounded" />
                <button onClick={() => setLocalQty((Number(qty) || 0) + 1)} className="p-1.5 hover:bg-muted rounded-r-full" data-testid={`qty-plus-${p.reference}`}><Plus size={13} /></button>
              </div>
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{t("product.boxes")}</span>
            </div>
            <button onClick={add} disabled={busy} data-testid={`add-to-cart-${p.reference}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 self-start mt-0.5">
              <ShoppingCart size={13} /> {t("product.add")}
            </button>
          </div>
          )}
          {!isStaff && (
            <p className="text-[11px] text-emerald-600 font-medium min-h-[1rem]" data-testid={`in-cart-${p.reference}`}>
              {inCart ? `${t("product.inCart")} ${inCart.qty_boxes} ${t("product.boxes")} (${unitsTotal(p, inCart.qty_boxes)} ${unit})` : ""}
            </p>
          )}
          {showNote && (
            <div className="flex items-center gap-1.5 border-t border-dashed border-border pt-2" data-testid={`fav-note-wrap-${p.reference}`}>
              <StickyNote size={13} className="text-amber-600 shrink-0" />
              <input value={noteDraft} maxLength={120} onChange={(e) => setNoteDraft(e.target.value)}
                onBlur={() => { if (noteDraft !== (note || "")) onSaveNote(p, noteDraft); }}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                placeholder={t("fav.notePh")} data-testid={`fav-note-${p.reference}`}
                className="flex-1 min-w-0 bg-transparent text-xs px-1 py-1 border-b border-transparent focus:border-amber-500 focus:outline-none" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Lightbox({ p, onClose }) {
  const { items, setQty } = useCart();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const { t } = useLang();
  const inCart = items.find((i) => i.product.id === p.id);
  const [qty, setLocalQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const unit = unitOf(p);
  const gallery = (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []));
  const [idx, setIdx] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const currentSrc = imgUrl(gallery[idx] || p.image_url);
  useEffect(() => { setImgLoaded(false); }, [currentSrc]);
  useEffect(() => {
    if (gallery.length < 2) return;
    [(idx + 1) % gallery.length, (idx - 1 + gallery.length) % gallery.length].forEach((i) => { const im = new Image(); im.src = imgUrl(gallery[i]); });
  }, [idx, gallery]);

  const add = async () => {
    setBusy(true);
    try {
      await setQty(p.id, (inCart?.qty_boxes || 0) + (Number(qty) || 1));
      toast.success(`${p.reference}: ${qty} ${t("product.boxes")} ${t("toast.added")}`);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="image-lightbox">
      <div className="relative fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <div className="relative rounded-2xl overflow-hidden bg-white" style={{ width: "min(800px, 90vw)", height: "min(800px, 72vh)" }}>
            {!imgLoaded && <div className="absolute inset-0 skeleton-shimmer" data-testid="image-lightbox-skeleton" />}
            <img src={currentSrc} alt={p.name} decoding="async" onLoad={() => setImgLoaded(true)}
              data-testid="image-lightbox-img"
              className={`w-full h-full object-contain transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`} />
          </div>
          {gallery.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + gallery.length) % gallery.length); }} data-testid="lightbox-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 rounded-full w-9 h-9 shadow hover:scale-110 transition-transform text-2xl leading-none">‹</button>
              <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % gallery.length); }} data-testid="lightbox-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 rounded-full w-9 h-9 shadow hover:scale-110 transition-transform text-2xl leading-none">›</button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="lightbox-dots">
                {gallery.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-amber-500" : "w-1.5 bg-white/70"}`} />)}
              </div>
            </>
          )}
        </div>
        <button onClick={onClose} data-testid="image-lightbox-close"
          className="absolute -top-3 -right-3 bg-white text-slate-900 rounded-full p-2 shadow-lg hover:scale-110 transition-transform">
          <X size={18} />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-slate-900/90 text-white rounded-b-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="min-w-0 flex-1 text-left" data-testid="lightbox-details">
            <p className="text-base font-bold uppercase tracking-wide leading-tight" data-testid="lightbox-name">{p.name}</p>
            <dl className="mt-1.5 space-y-0.5 text-sm">
              <div className="flex gap-2"><dt className="font-bold uppercase text-amber-300">{t("product.reference")}:</dt><dd className="font-mono" data-testid="lightbox-reference">{p.reference}</dd></div>
              <div className="flex gap-2"><dt className="font-bold uppercase text-amber-300">{t("product.size")}:</dt><dd data-testid="lightbox-size">{p.size || "—"}</dd></div>
              <div className="flex gap-2"><dt className="font-bold uppercase text-amber-300">{t("product.packing")}:</dt><dd data-testid="lightbox-packing">{p.packing > 0 ? `${p.packing} ${unit.toUpperCase()}` : t("product.packingNA")}</dd></div>
              <div className="flex gap-2"><dt className="font-bold uppercase text-amber-300">{t("product.price")}:</dt><dd className="font-semibold" data-testid="lightbox-price">${p.price_dozen.toFixed(2)}/{unit.toUpperCase()}</dd></div>
            </dl>
            {inCart && <p className="text-emerald-400 font-medium text-xs mt-1" data-testid="lightbox-in-cart">{t("product.inCart")} {inCart.qty_boxes} {t("product.boxes")} ({unitsTotal(p, inCart.qty_boxes)} {unit})</p>}
          </div>
          <div className="flex items-center gap-2" style={{ display: isStaff ? "none" : undefined }}>
            <div className="flex flex-col items-center">
              <div className="flex items-center border border-white/30 rounded-full">
                <button onClick={() => setLocalQty(Math.max(1, (Number(qty) || 1) - 1))} className="p-2 hover:bg-white/10 rounded-l-full" data-testid="lightbox-qty-minus"><Minus size={14} /></button>
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={qty} data-testid="lightbox-qty-value"
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setLocalQty(v === "" ? "" : Math.max(1, parseInt(v, 10))); }}
                  onBlur={() => { if (!qty) setLocalQty(1); }}
                  className="w-12 px-1 py-1 text-sm font-semibold text-center bg-transparent text-white focus:outline-none focus:ring-1 focus:ring-amber-400 rounded" />
                <button onClick={() => setLocalQty((Number(qty) || 0) + 1)} className="p-2 hover:bg-white/10 rounded-r-full" data-testid="lightbox-qty-plus"><Plus size={14} /></button>
              </div>
              <span className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">{t("product.boxes")}</span>
            </div>
            <button onClick={add} disabled={busy} data-testid="lightbox-add-to-cart"
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 self-start">
              <ShoppingCart size={15} /> {t("product.add")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(null);
  const [showAllCats, setShowAllCats] = useState(false);
  const { favIds, notes, toggle, setNote } = useFavorites();
  const saveNote = async (p, note) => {
    try { await setNote(p.id, note); toast.success(`${p.reference}: ${t("fav.noteSaved")}`); } catch (e) { toast.error(errDetail(e)); }
  };
  const { refresh: refreshCart } = useCart();
  const { user } = useAuth();
  const [favBusy, setFavBusy] = useState(false);
  const [alertIds, setAlertIds] = useState([]);
  useEffect(() => {
    api.get("/restock-alerts").then(({ data }) => setAlertIds(data.product_ids)).catch(() => {});
  }, []);
  const toggleAlert = async (p) => {
    try {
      const { data } = await api.put(`/restock-alerts/${p.id}`);
      setAlertIds((ids) => data.subscribed ? [...ids, p.id] : ids.filter((i) => i !== p.id));
      toast.success(`${p.reference}: ${data.subscribed ? t("fav.notifyOk") : t("fav.notifyOff")}`);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const [params, setParams] = useSearchParams();
  const onlyFavs = params.get("fav") === "1";
  const setOnlyFavs = (v) => setParams(v ? { fav: "1" } : {});
  useEffect(() => { if (onlyFavs) { setCategory(""); setSearch(""); setShowAllCats(false); } }, [onlyFavs]);

  const favsToCart = async () => {
    setFavBusy(true);
    try {
      const { data } = await api.post("/favorites/to-cart");
      await refreshCart();
      toast.success(`${data.added} ${t("fav.addedToCart")}`);
    } catch (e) { toast.error(errDetail(e)); }
    setFavBusy(false);
  };

  const favsPdf = async () => {
    setFavBusy(true);
    try {
      const res = await api.get("/favorites/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "favoritos.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("fav.pdfOk"));
    } catch (e) { toast.error(errDetail(e)); }
    setFavBusy(false);
  };

  const toggleFav = async (p) => {
    try {
      const fav = await toggle(p.id);
      toast.success(`${p.reference}: ${fav ? t("product.favAdded") : t("product.favRemoved")}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/products", { params: { category: category || undefined, search: search || undefined, fav: onlyFavs || undefined } })
        .then(({ data }) => setProducts(data))
        .catch((e) => toast.error(errDetail(e)))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, search, onlyFavs]);

  const pills = useMemo(() => ["", ...categories], [categories]);
  const pickCategory = (c) => { setCategory(c); setShowAllCats(false); };
  const visible = onlyFavs ? products.filter((p) => favIds.includes(p.id)) : products;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="catalog-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">{t("catalog.title")}</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button onClick={() => setOnlyFavs(!onlyFavs)} data-testid="favorites-filter-button" aria-pressed={onlyFavs}
            className={`shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold border transition-colors ${onlyFavs ? "bg-rose-500 text-white border-transparent" : "border-rose-400 text-rose-500 hover:bg-rose-500/10"}`}>
            <Heart size={14} fill={onlyFavs ? "currentColor" : "none"} /> {t("catalog.favorites")} <span data-testid="favorites-count">({favIds.length})</span>
          </button>
        <div className="relative flex-1 sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalog-search-input"
            placeholder={t("catalog.searchPlaceholder")}
            className="w-full pl-9 pr-4 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
        </div>
        </div>
      </div>

      {onlyFavs && favIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-2xl px-4 py-3 fade-up" data-testid="favorites-actions-bar">
          <Heart size={16} className="text-rose-500" fill="currentColor" />
          <span className="text-sm font-medium flex-1">{t("catalog.favorites")}: {favIds.length}</span>
          {user?.role !== "staff" && (
            <button onClick={favsToCart} disabled={favBusy} data-testid="favorites-add-all-to-cart"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50">
              <ShoppingCart size={14} /> {t("fav.allToCart")}
            </button>
          )}
          <button onClick={favsPdf} disabled={favBusy} data-testid="favorites-export-pdf"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-rose-400 text-rose-600 dark:text-rose-300 text-xs font-semibold hover:bg-rose-500/10 disabled:opacity-50">
            <FileDown size={14} /> {t("fav.exportPdf")}
          </button>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex gap-2 overflow-x-auto pb-1 flex-1 min-w-0" data-testid="category-filter-bar">
            {pills.map((c) => (
              <button key={c || "all"} onClick={() => pickCategory(c)} data-testid={`category-pill-${c || "all"}`}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${category === c ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                {c || t("catalog.all")}
              </button>
            ))}
          </div>
          <button onClick={() => setShowAllCats(!showAllCats)} data-testid="show-all-categories-button"
            className={`shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${showAllCats ? "bg-amber-600 text-white border-transparent" : "border-amber-600 text-amber-600 hover:bg-amber-600/10"}`}>
            {showAllCats ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showAllCats ? t("catalog.hide") : `${t("catalog.viewAll")} (${categories.length})`}
          </button>
        </div>

        {showAllCats && (
          <div className="bg-card border border-border rounded-2xl p-3 sm:p-4 fade-up" data-testid="all-categories-panel">
            <div className="flex flex-wrap gap-2">
              {pills.map((c) => (
                <button key={c || "all"} onClick={() => pickCategory(c)} data-testid={`category-panel-pill-${c || "all"}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${category === c ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                  {c || t("catalog.all")}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">{t("catalog.loading")}</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="catalog-empty-state">
          {onlyFavs ? t("catalog.noFavs") : <>{t("catalog.empty")} {categories.length === 0 && t("catalog.emptyAdmin")}</>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {visible.map((p) => <ProductCard key={p.id} p={p} onZoom={setZoom} isFav={favIds.includes(p.id)} onToggleFav={toggleFav} alertOn={alertIds.includes(p.id)} onToggleAlert={toggleAlert} showNote={onlyFavs} note={notes[p.id]} onSaveNote={saveNote} />)}
        </div>
      )}

      {zoom && <Lightbox p={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}
